import type { SurveyAttribute, SurveyScoresByCategoryXAttributes } from '@engagement-web-app/common';
import { useMemo, useState } from 'react';
import { ErrorOverlay } from '@/components/ErrorOverlay';
import { Flex } from '@/components/Flex';
import { Pill } from '@/components/Pill';
import { Section, SectionHeader } from '@/components/Section';
import { Skeleton } from '@/components/Skeleton';
import { Table } from '@/components/Table';
import { fitTableToPageInPrint } from '@/styles/print.css';
import type { ScoreKey, SortOrder } from '@/utils';
import { ComparisonTable } from '../common/ComparisonTable';
import { AttributeFilter, AttributeFilterSkeleton } from './AttributeFilter';

const SKELETON_ROW_COUNT = 12;

type SurveyCategoriesComparisonTableProps = {
    /**
     * `null` until the first fetch for the current filter resolves. The underlying query
     * keeps the previous filter's data around during a refetch, so this doesn't go back
     * to `null` on a filter change — see the note on `CategoryScoresTable`.
     */
    scores: SurveyScoresByCategoryXAttributes | null;
    /** `null` when there's no previous survey to compare against, or its own filtered fetch hasn't resolved. */
    previousScores: SurveyScoresByCategoryXAttributes | null;
    /** `null` while still loading. */
    attributeSettings: SurveyAttribute[] | null;
    selectedAttributeOptionIds: string[];
    onSelectedAttributeOptionIdsChange: (ids: string[]) => void;
    /** True while `scores`/`previousScores` are being (re)fetched for the current filter. */
    isFetching: boolean;
    isError: boolean;
    onRetry: () => void;
};

// Pure composition: `attributeSettings` and `scores` are independent loading
// states (the former loads once, the latter reloads on every filter change).
export function SurveyCategoriesComparisonTable({
    scores,
    previousScores,
    attributeSettings,
    selectedAttributeOptionIds,
    onSelectedAttributeOptionIdsChange,
    isFetching,
    isError,
    onRetry,
}: SurveyCategoriesComparisonTableProps) {
    if (isError) return <ErrorOverlay active onRetry={onRetry} message="カテゴリー別スコアの取得に失敗しました。" />;

    return (
        <Section>
            <SectionHeader
                title="カテゴリー別スコア"
                description="カテゴリーごとの DI・偏差値・回答割合を一覧し、前回サーベイからの差を併記します。属性での絞り込みと各列での並べ替えができます。絞り込んだ結果の回答者が 3 人未満の場合、スコアは表示されません。"
            />

            <Flex direction="column" gap="6">
                {attributeSettings ? (
                    <AttributeFilterSection
                        attributeSettings={attributeSettings}
                        selectedAttributeOptionIds={selectedAttributeOptionIds}
                        onSelectedAttributeOptionIdsChange={onSelectedAttributeOptionIdsChange}
                    />
                ) : (
                    <AttributeFilterSectionSkeleton />
                )}

                {scores ? (
                    <CategoryScoresTable scores={scores} previousScores={previousScores} isFetching={isFetching} />
                ) : (
                    // Reached only before the first fetch ever resolves — once `scores` has a value,
                    // the underlying query keeps it around during a refetch, so this never flashes
                    // back in on a filter change.
                    <CategoryScoresTableSkeleton />
                )}
            </Flex>
        </Section>
    );
}

/**
 * A placeholder for the whole card, for the page's initial load — distinct from the
 * partial loading the component handles internally once it's on screen.
 */
export function SurveyCategoriesComparisonTableSkeleton() {
    return (
        <Section>
            <Flex direction="column" gap="6">
                <Flex direction="column" gap="1">
                    <Skeleton.Line width="40%" height="xl" />
                    <Skeleton.Line width="70%" height="sm" />
                </Flex>

                <AttributeFilterSectionSkeleton />

                <Skeleton.Line width={120} height="xs" />

                <Table.Skeleton columns={4} rows={SKELETON_ROW_COUNT} />
            </Flex>
        </Section>
    );
}

type AttributeFilterSectionProps = {
    attributeSettings: SurveyAttribute[];
    selectedAttributeOptionIds: string[];
    onSelectedAttributeOptionIdsChange: (ids: string[]) => void;
};

function AttributeFilterSection({
    attributeSettings,
    selectedAttributeOptionIds,
    onSelectedAttributeOptionIdsChange,
}: AttributeFilterSectionProps) {
    const selectedOptions = useMemo(
        () =>
            attributeSettings.flatMap((setting) =>
                setting.options
                    .filter((o) => selectedAttributeOptionIds.includes(o.id))
                    .map((o) => ({ ...o, settingName: setting.name }))
            ),
        [attributeSettings, selectedAttributeOptionIds]
    );

    function handleSelectionChange(options: SurveyAttribute['options']) {
        onSelectedAttributeOptionIdsChange(options.map((o) => o.id));
    }

    return (
        <Flex direction="column" gap="4" align="start">
            <AttributeFilter
                attributes={attributeSettings}
                selectedOptionIds={selectedAttributeOptionIds}
                onSelectionChange={handleSelectionChange}
            />

            {selectedOptions.length > 0 && (
                <Flex gap="2" wrap="wrap">
                    {selectedOptions.map((option) => (
                        <Pill variant="quinary" key={option.id}>
                            {option.settingName}：{option.name}
                        </Pill>
                    ))}
                </Flex>
            )}
        </Flex>
    );
}

function AttributeFilterSectionSkeleton() {
    return (
        <Flex direction="column" gap="4" align="start">
            <AttributeFilterSkeleton />
        </Flex>
    );
}

type CategoryScores = SurveyScoresByCategoryXAttributes['categories'][number];

type SortKey = ScoreKey | 'favorable';

type CategoryScoresTableProps = {
    scores: SurveyScoresByCategoryXAttributes;
    /** `null` when there's no previous survey to compare against. Not a loading state. */
    previousScores: SurveyScoresByCategoryXAttributes | null;
    /** True while `scores`/`previousScores` are being (re)fetched for the current filter. */
    isFetching: boolean;
};

function CategoryScoresTable({ scores, previousScores, isFetching }: CategoryScoresTableProps) {
    const [sortKey, setSortKey] = useState<SortKey | null>(null);
    const [sortDir, setSortDir] = useState<SortOrder | null>(null);

    function handleSort(key: SortKey) {
        if (sortKey !== key) {
            setSortKey(key);
            setSortDir('desc');
        } else if (sortDir === 'desc') {
            setSortDir('asc');
        } else {
            setSortKey(null);
            setSortDir(null);
        }
    }

    function directionFor(key: SortKey) {
        return sortKey === key ? sortDir : null;
    }

    return (
        <>
            <Table.RowCount label="カテゴリー数" count={scores.categories.length} unit="件" />

            <Skeleton.Overlay active={isFetching}>
                <Table.Root className={fitTableToPageInPrint}>
                    <Table.Header>
                        <Table.HeaderCell>カテゴリー</Table.HeaderCell>
                        <Table.HeaderCell>
                            <Table.SortButton onClick={() => handleSort('di')} direction={directionFor('di')}>
                                DI
                            </Table.SortButton>
                        </Table.HeaderCell>
                        <Table.HeaderCell>
                            <Table.SortButton
                                onClick={() => handleSort('deviation')}
                                direction={directionFor('deviation')}
                            >
                                偏差値
                            </Table.SortButton>
                        </Table.HeaderCell>
                        <Table.HeaderCell>
                            <Table.SortButton
                                onClick={() => handleSort('favorable')}
                                direction={directionFor('favorable')}
                            >
                                回答割合
                            </Table.SortButton>
                        </Table.HeaderCell>
                    </Table.Header>

                    <Table.Body>
                        <CategoryRows
                            scores={scores}
                            previousScores={previousScores}
                            sortKey={sortKey}
                            sortDir={sortDir}
                        />
                    </Table.Body>
                </Table.Root>
            </Skeleton.Overlay>
        </>
    );
}

function CategoryScoresTableSkeleton() {
    return (
        <>
            <Skeleton.Line width={120} height="xs" />
            <Table.Skeleton columns={4} rows={SKELETON_ROW_COUNT} />
        </>
    );
}

type CategoryRowsProps = Omit<CategoryScoresTableProps, 'scores' | 'isFetching'> & {
    scores: SurveyScoresByCategoryXAttributes;
    sortKey: SortKey | null;
    sortDir: SortOrder | null;
};

function CategoryRows({ scores, previousScores, sortKey, sortDir }: CategoryRowsProps) {
    const previousCategoryMap = useMemo<Map<string, CategoryScores>>(() => {
        if (!previousScores) return new Map();
        return new Map(previousScores.categories.map((c) => [c.id, c]));
    }, [previousScores]);

    const categories = useMemo<CategoryScores[]>(() => {
        if (sortKey === null || sortDir === null) return scores.categories;
        return [...scores.categories].sort((a, b) => {
            let aVal: number;
            let bVal: number;
            if (sortKey === 'favorable') {
                aVal = a.distribution?.favorable ?? -Infinity;
                bVal = b.distribution?.favorable ?? -Infinity;
            } else {
                aVal = a[sortKey] ?? -Infinity;
                bVal = b[sortKey] ?? -Infinity;
            }
            return sortDir === 'desc' ? bVal - aVal : aVal - bVal;
        });
    }, [scores, sortKey, sortDir]);

    return (
        <>
            {categories.map((category) => {
                const previous = category.previousCategoryId
                    ? previousCategoryMap.get(category.previousCategoryId)
                    : null;

                return (
                    <Table.Row key={category.id}>
                        <Table.Cell>
                            <Flex direction="column" align="start" gap="2">
                                <span title={category.name}>{category.name}</span>
                                <Pill variant="quinary" weight="bold" size="small">
                                    {category.questionCount}問
                                </Pill>
                            </Flex>
                        </Table.Cell>
                        <Table.Cell>
                            <ComparisonTable.ScoreCell value={category.di} previousValue={previous?.di ?? null} />
                        </Table.Cell>
                        <Table.Cell>
                            <ComparisonTable.ScoreCell
                                value={category.deviation}
                                previousValue={previous?.deviation ?? null}
                            />
                        </Table.Cell>
                        <Table.Cell>
                            {category.distribution && (
                                <ComparisonTable.DistributionCell distribution={category.distribution} />
                            )}
                        </Table.Cell>
                    </Table.Row>
                );
            })}
        </>
    );
}
